import { inject } from "@angular/core";
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from "rxjs";
import { ApiException, AuthClient, SessionToken, UserInfo } from "./api.service.gen";

export class Me {
  constructor(public name: string, public providerName: string) {
  }
}

export enum MeLoadState {
  RefreshMe = 0,
  ReloadMe = 1,
  Loaded = 2,
  ErrorUnauthenticated = 3,
  ErrorUnknown = 4
}

export interface MeState {
  me: Me | null;
  isAuthenticated: boolean | null;
  isLoading: boolean;
  zumo: string | null 
  meLoadState: MeLoadState;
  timer: ReturnType<typeof setTimeout> | null
}

const initialState: MeState = {
  me: null, 
  isAuthenticated: null,
  isLoading: false,
  zumo: null,
  meLoadState: MeLoadState.Loaded,
  timer: null
};

export const MeStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, client = inject(AuthClient)) => ({
    reloadMeRx: rxMethod<MeLoadState.ReloadMe>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() => {
          return client.me().pipe(
            tapResponse<UserInfo, ApiException>({
              next: (userInfo) => {
                if (userInfo.authenticated) {
                  const providerName = userInfo.identityProviderName || '';
                  const user_name = userInfo.userName || '';
                  const me = new Me(user_name, providerName);
                  patchState(store, { me: me, isAuthenticated: true, meLoadState: MeLoadState.Loaded, timer: null });
                } else {
                  patchState(store, { isAuthenticated: false, me: null, meLoadState: MeLoadState.ErrorUnauthenticated });
                }
              },
              error: (error) => {
                patchState(store, { isAuthenticated: false, me: null, meLoadState: GetErrorState(error) });
              },
              finalize: () => patchState(store, { isLoading: false }),
            })
          );
        })
      )
    ),
    reloadMe(): void {
      patchState(store, { meLoadState: MeLoadState.ReloadMe, isLoading: true});
    },
    setZumoFromTokenFragment( fragment: string ): void {
     const sessionToken = decodeURIComponent(fragment).substring("token=".length);
     const r = SessionToken.fromJS(JSON.parse(sessionToken));
     patchState(store, { zumo: r.authenticationToken });
    },
  })),
);

function GetErrorState(error: ApiException) {
  let loadState = MeLoadState.ErrorUnknown;
  if (error.status === 401) {
    loadState = MeLoadState.ErrorUnauthenticated;
  }
  return loadState;
}
